import { Check, ListFilter, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { deriveTaskLifecycle } from "@shared/taskLifecycle";

export type TaskFilterStatus = "pending" | "doing" | "done" | "skipped";
export type TaskScheduleFilter = "all" | "scheduled" | "unscheduled";
export type TaskFilterState = {
  statuses: TaskFilterStatus[];
  listIds: Array<number | null>;
  schedule: TaskScheduleFilter;
};

type FilterableTask = {
  status: "pending" | "done" | "skipped";
  startedAt?: Date | string | null;
  completedAt?: Date | string | null;
  startMinutes?: number | null;
  listId?: number | null;
};

type ListOption = {
  id: number | null;
  name: string;
  color?: string | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: TaskFilterState;
  onChange: (next: TaskFilterState) => void;
  lists: ListOption[];
  showSchedule?: boolean;
  title?: string;
  description?: string;
};

export const EMPTY_TASK_FILTER: TaskFilterState = {
  statuses: [],
  listIds: [],
  schedule: "all",
};

const STATUS_OPTIONS: Array<{ value: TaskFilterStatus; label: string; description: string }> = [
  { value: "pending", label: "Pending", description: "Not started" },
  { value: "doing", label: "Started", description: "In progress" },
  { value: "done", label: "Done", description: "Finished" },
  { value: "skipped", label: "Skipped", description: "Set aside" },
];

export function activeTaskFilterCount(value: TaskFilterState) {
  return value.statuses.length + value.listIds.length + (value.schedule === "all" ? 0 : 1);
}

export function matchesTaskFilter<T extends FilterableTask>(task: T, value: TaskFilterState) {
  if (value.statuses.length > 0) {
    const lifecycle = deriveTaskLifecycle(task);
    if (!value.statuses.includes(lifecycle)) return false;
  }
  if (value.listIds.length > 0 && !value.listIds.includes(task.listId ?? null)) return false;
  if (value.schedule === "scheduled" && task.startMinutes == null) return false;
  if (value.schedule === "unscheduled" && task.startMinutes != null) return false;
  return true;
}

function choiceClass(selected: boolean) {
  return [
    "flex w-full items-center gap-3 rounded-xl border px-3 py-3 text-left transition-colors",
    selected
      ? "border-foreground bg-foreground text-background shadow-sm"
      : "border-border bg-card hover:border-foreground/40 hover:bg-muted/50",
  ].join(" ");
}

function toggleValue<T>(items: T[], value: T) {
  return items.includes(value) ? items.filter(item => item !== value) : [...items, value];
}

export default function TaskFilterSheet({
  open,
  onOpenChange,
  value,
  onChange,
  lists,
  showSchedule = true,
  title = "Filter tasks",
  description,
}: Props) {
  const activeCount = activeTaskFilterCount(value);
  const reset = () => onChange(EMPTY_TASK_FILTER);
  const toggleStatus = (status: TaskFilterStatus) => {
    onChange({ ...value, statuses: toggleValue(value.statuses, status) });
  };
  const toggleArea = (listId: number | null) => {
    onChange({ ...value, listIds: toggleValue(value.listIds, listId) });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" showClose={false} className="flex max-h-[86dvh] flex-col overflow-hidden rounded-t-2xl pb-0">
        <SheetHeader className="shrink-0 border-b border-border/70 pb-4">
          <div className="flex items-center gap-2 pr-10">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-muted">
              <ListFilter className="h-4 w-4" />
            </span>
            <div>
              <SheetTitle>{title}</SheetTitle>
              <SheetDescription>
                {description ?? (activeCount === 0 ? "Show exactly what you need." : `${activeCount} active filter${activeCount === 1 ? "" : "s"}.`)}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain space-y-7 px-4 py-5">
          <section aria-labelledby="filter-status-label">
            <h3 id="filter-status-label" className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Status</h3>
            <div className="mt-3 grid grid-cols-2 gap-2">
              {STATUS_OPTIONS.map(option => {
                const selected = value.statuses.includes(option.value);
                return (
                  <button key={option.value} type="button" className={choiceClass(selected)} onClick={() => toggleStatus(option.value)} aria-pressed={selected}>
                    <span className="flex-1">
                      <span className="block text-sm font-medium">{option.label}</span>
                      <span className={selected ? "block text-xs text-background/70" : "block text-xs text-muted-foreground"}>{option.description}</span>
                    </span>
                    {selected && <Check className="h-4 w-4 shrink-0" aria-hidden />}
                  </button>
                );
              })}
            </div>
          </section>

{showSchedule && (          <section aria-labelledby="filter-schedule-label">
            <h3 id="filter-schedule-label" className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Schedule</h3>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {(["all", "scheduled", "unscheduled"] as const).map(option => {
                const selected = value.schedule === option;
                const label = option === "all" ? "All" : option === "scheduled" ? "Scheduled" : "Unscheduled";
                return (
                  <button key={option} type="button" className={["rounded-lg border px-2 py-2.5 text-sm font-medium transition-colors", selected ? "border-foreground bg-foreground text-background" : "border-border bg-card hover:border-foreground/40 hover:bg-muted/50"].join(" ")} onClick={() => onChange({ ...value, schedule: option })} aria-pressed={selected}>
                    {label}
                  </button>
                );
              })}
            </div>
          </section>)}

          <section aria-labelledby="filter-area-label">
            <div className="flex items-center justify-between gap-3">
              <h3 id="filter-area-label" className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Lists</h3>
              {value.listIds.length > 0 && <span className="text-xs text-muted-foreground">{value.listIds.length} selected</span>}
            </div>
            <div className="mt-3 space-y-2">
              <button type="button" className={choiceClass(value.listIds.length === 0)} onClick={() => onChange({ ...value, listIds: [] })} aria-pressed={value.listIds.length === 0}>
                <span className="h-3 w-3 rounded-full border border-current opacity-70" />
                <span className="flex-1 text-sm font-medium">All lists</span>
                {value.listIds.length === 0 && <Check className="h-4 w-4" aria-hidden />}
              </button>
              {lists.map(list => {
                const selected = value.listIds.includes(list.id);
                return (
                  <button key={list.id ?? "inbox"} type="button" className={choiceClass(selected)} onClick={() => toggleArea(list.id)} aria-pressed={selected}>
                    <span className="h-3 w-3 rounded-full" style={{ background: list.color ?? "var(--muted-foreground)" }} />
                    <span className="flex-1 text-sm font-medium">{list.name}</span>
                    {selected && <Check className="h-4 w-4" aria-hidden />}
                  </button>
                );
              })}
            </div>
          </section>
        </div>

        <SheetFooter className="shrink-0 border-t border-border/70 bg-background/95 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 backdrop-blur">
          <div className="flex gap-2">
            <Button type="button" variant="outline" className="flex-1" onClick={reset} disabled={activeCount === 0}>
              <RotateCcw className="mr-2 h-4 w-4" /> Reset
            </Button>
            <Button type="button" className="flex-1" onClick={() => onOpenChange(false)}>Done</Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
